import React from 'react';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import Card from 'src/components/common/Card.redesign';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import FormRow from 'src/components/controls/forms/FormRow';
import FormCheckbox from 'src/components/controls/forms/redesign/FormCheckbox';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import FormTextBox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';
import { AnyObject, Field } from 'react-final-form';
import { SelectItem } from 'src/models/common/SelectItem';
import { FieldArray } from 'react-final-form-arrays';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import SurfaceOwnershipCard from './SurfaceOwnershipCard';
import AnimateHeight from 'react-animate-height';
import { FormApi, getIn } from 'final-form';
import FormTextArea from 'src/components/controls/forms/redesign/FormTextArea';
import { LandSystem } from 'src/models/LandSystem';
import Ownership from 'src/models/tract_detail/Ownership';
import MathUtil from 'src/util/MathUtil';
import { OwnershipCategoryType } from 'src/models/OwnershipCategory';
import SurfaceTract from 'src/models/SurfaceTract';
import Button from 'src/components/controls/forms/redesign/FormButton';

interface ISurfaceTractCardProps {
    id: number;
    number: number;
    surfaceTractAcreage: number;
    onSurfaceTractDeleted: () => void;
    namePrefix: string;
    landSystem: LandSystem;
    formApi: FormApi;
    contactCategories: Array<SelectItem>;
    categoryTypes: Array<OwnershipCategoryType>;
    leaseBuyers: Array<SelectItem>;
    makeDirty: () => void;
    openContactNotes: (ownership: Ownership) => void;
    updateTractPage: () => void;
    openOwnershipNotes: (ownership: Ownership, surfaceTractId: number) => void;
}

const SurfaceTractCard: React.FC<ISurfaceTractCardProps> = (props) => {
    let ownershipFields: AnyObject = undefined;

    const appContext = useSelector((state: IRootState) => state.appContext);
    const { post, del, response } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const [showOwners, setShowOwners] = React.useState<boolean>(false);
    const [recordType, setRecordType] = React.useState(0);
    const [isPartialAcreage, setIsPartialAcreage] = React.useState<boolean>(false);

    // TODO: Set this up so it isn't hard-coded.
    const determineOwnerType = (id: number) => {
        let type = 'Unknown';
        switch (id) {
            case 1:
                type = 'Heir';
                break;
            case 2:
                type = 'Remainderman';
                break;
            case 3:
                type = 'Non-Executive';
                break;
            case 4:
                type = 'Non-Participating';
                break;
            case 5:
                type = 'Transfer On Death';
                break;
            case 6:
                type = 'Naked Owner';
                break;
            default:
                break;
        }

        return type;
    };
    
    React.useEffect(() => {
        setRecordType(parseInt(props.formApi.getFieldState(`${props.namePrefix}.recordTypeId`).value, 10));
        setIsPartialAcreage(props.formApi.getFieldState(`${props.namePrefix}.isPartial`)?.value || false);
    }, []);

    const deleteSurfaceTract = async () => {
        if (!!ownershipFields && ownershipFields.length > 0) {
            window.alert('Please delete ownerships before detaching surface tract');
        } else {
            const confirmAction = async () => {
                await del(`${Url.tract.getTract(appContext)}/surfaceTracts/${props.id}`);
    
                if (response.ok) {
                    if (props.onSurfaceTractDeleted) {
                        props.onSurfaceTractDeleted();
                    }
                }
            };
    
            const body = (
                <>
                    <div style={{ fontWeight: 'bold' }}>
                        Are you sure you want to remove Surface {tractDescription()} {props.id}?
                    </div>
                </>
            );
    
            dialogContext.common.confirmAction(`Delete Surface ${tractDescription()}`, body, confirmAction);
        }
    };

    const tractDescription = () => {
        return props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract';
    };

    const tractLabelDescription = () => {
        return props.landSystem === LandSystem.Eastern ? 'Parcel Id' : 'Tract Name';
    };

    const addOwnership = (parentOwnershipId: number = 0) => {
        dialogContext.tract.createSurfaceOwnership(props.id, parentOwnershipId, async (ownership: Ownership) => {
            if (ownershipFields) {
                if (parentOwnershipId > 0) {
                    ownership.parentOwnershipId = parentOwnershipId;
                }
                await post(`${Url.tract.getTract(appContext)}/surfaceTracts/${props.id}/ownerships`, ownership);

                if (response.ok) {
                    props.updateTractPage();
                }
                setShowOwners(true);
            }
        });
    };

    const actions: ContextMenuAction[] = [
        { text: 'Add Ownership', action: addOwnership },
        { text: `Delete Surface ${tractDescription()}`, action: deleteSurfaceTract },
    ];

    const fixNumberForUIDisplay = (value: string) => {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
            return numericValue.toFixed(7).toString();
        } else {
            return '';
        }
    };

    const ownershipInterest = (interest: number, acres: number) => {
        const status = interest === 1 ? '#d1fdda' : interest < 1 ? '#ffeb9c' : '#ffc7ce';

        const interestString = isNaN(interest) ? '---' : fixNumberForUIDisplay(MathUtil.cleanNumber(interest, 10, 0));
        const acresString = isNaN(acres) ? '---' : fixNumberForUIDisplay(MathUtil.cleanNumber(acres, 10, 0));

        return (
            <div>
                <span
                    className="mr-1"
                    style={{ textAlign: 'left', display: 'inline-block', minWidth: '18.4rem', padding: '1rem', backgroundColor: status }}
                >
                    Total Interest: {interestString}
                </span>
                <span
                    className="mr-1"
                    style={{ textAlign: 'left', display: 'inline-block', minWidth: '18.4rem', padding: '1rem', backgroundColor: status }}
                >
                    Total Acres: {acresString}
                </span>
            </div>
        );
    };

    const updateIsPartial = (isPartial: boolean) => {
        setIsPartialAcreage(isPartial);
    };

    const openSurfaceTractNotes = () => {
        const surfaceTract = getIn(props.formApi.getState().values, props.namePrefix) as SurfaceTract;

        dialogContext.tract.openTractNotes(
            undefined,
            surfaceTract,
            '',
            null,
            handleNoteUpdate,
            null,
            true
        );
    };

    const handleNoteUpdate = (updated: SurfaceTract) => {
        props.formApi.change(`${props.namePrefix}.note`, updated.note);
        props.makeDirty();
    };

    return (
        <Card contextActions={actions} newContext={true} showMenu={true} badge={props.number?.toString()}>
            <div style={{ padding: '1rem', boxSizing: 'border-box' }}>
                <div style={{ display: 'none' }}>
                    <Field name={`${props.namePrefix}.id`} component="input" />
                    <Field name={`${props.namePrefix}.number`} component="input" />
                    <Field name={`${props.namePrefix}.note`} component="input" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <Button intent="primary" size="medium" text="Surface Tract Notes" onClick={openSurfaceTractNotes} />
                </div>
                <FormRow>
                    <FormTextBox label={tractLabelDescription()} name={`${props.namePrefix}.name`} style={{ width: '30%' }} />
                    <FormTextBox type={FormTextBoxDataType.Float} label="Acres" name={`${props.namePrefix}.acres`} style={{ width: '30%' }} />
                    <FormCheckbox
                        label="Partial"
                        name={`${props.namePrefix}.isPartial`}
                        wrapped={true}
                        style={{ width: 'calc(20% - 4rem)' }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateIsPartial(e.currentTarget.checked)}
                    />
                    {isPartialAcreage && (
                        <FormTextBox
                            type={FormTextBoxDataType.Float}
                            label="Partial Acreage"
                            name={`${props.namePrefix}.partialAcreage`}
                            style={{ width: '20%' }}
                        />
                    )}
                </FormRow>
                <FormRow>
                    <FormTextBox name={`${props.namePrefix}.grantor`} label="Grantor" style={{ width: '50%' }} />
                    <FormTextBox name={`${props.namePrefix}.grantee`} label="Grantee" style={{ width: '50%' }} />
                </FormRow>
                <FormRow>
                    <FormSelect
                        items={[new SelectItem(1, 'Book/Page'), new SelectItem(2, 'Instrument')]}
                        label="Inst. Type"
                        name={`${props.namePrefix}.recordTypeId`}
                        style={{ width: '25%' }}
                        onChange={(id) => setRecordType(id as number)}
                    />
                    {recordType === 1 && (
                        <>
                            <FormTextBox label="Book/Doc" name={`${props.namePrefix}.book`} style={{ width: '25%' }} />
                            <FormTextBox label="Page" name={`${props.namePrefix}.page`} style={{ width: '25%' }} />
                            <FormTextBox
                                type={FormTextBoxDataType.Date}
                                label="Date"
                                name={`${props.namePrefix}.recordDate`}
                                style={{ width: '25%' }}
                            />
                        </>
                    )}
                    {recordType === 2 && (
                        <>
                            <FormTextBox label="Instrument Number" name={`${props.namePrefix}.instrumentNumber`} style={{ width: '25%' }} />
                            <FormTextBox
                                type={FormTextBoxDataType.Date}
                                label="Date"
                                name={`${props.namePrefix}.recordDate`}
                                style={{ width: '25%' }}
                            />
                            <div style={{ width: '25%' }} />
                        </>
                    )}
                    {recordType !== 1 && recordType !== 2 && <div style={{ width: '75%' }} />}
                </FormRow>
                {props.landSystem === LandSystem.Eastern && (
                    <>
                        <FormRow className="row" style={{ marginTop: 0 }}>
                            <FormTextBox name={`${props.namePrefix}.boundByNorth`} label="Bound By North" style={{ width: '25%' }} />
                            <FormTextBox name={`${props.namePrefix}.boundBySouth`} label="Bound By South" style={{ width: '25%' }} />
                            <FormTextBox name={`${props.namePrefix}.boundByEast`} label="Bound By East" style={{ width: '25%' }} />
                            <FormTextBox name={`${props.namePrefix}.boundByWest`} label="Bound By West" style={{ width: '25%' }} />
                        </FormRow>
                    </>
                )}
                {props.landSystem !== LandSystem.Eastern && (
                    <>
                        <FormRow className="row">
                            <FormTextArea
                                height="10.6rem"
                                label="Legal Description"
                                name={`${props.namePrefix}.legalDescription`}
                                style={{ width: '100%' }}
                            />
                        </FormRow>
                    </>
                )}
                <FieldArray name={`${props.namePrefix}.ownerships`}>
                    {({ fields }) => {
                        ownershipFields = fields;

                        if (fields.length <= 0) {
                            setShowOwners(false);
                        }

                        let totalInterest = 0;
                        let totalAcres = 0;
                        if (props.formApi) {
                            fields.forEach((field, index: number) => {
                                let ownership = fields.value[index] as Ownership;
                                totalInterest += parseFloat(props.formApi.getFieldState(`${field}.decimalInterest`)?.value);
                                totalAcres += parseFloat(props.formApi.getFieldState(`${field}.netAcres`)?.value);

                                let addOwnerships = ownership.additionalOwnerships;
                                if (addOwnerships?.length > 0) {
                                    addOwnerships.forEach((addField: Ownership) => {
                                        const type = determineOwnerType(addField.additionalOwnershipTypeId);
                                        if (type === 'Non-Executive' || type === 'Heir') {
                                            totalInterest += parseFloat(addField.decimalInterest + '');
                                            totalAcres += parseFloat(addField.netAcres + '');
                                        }
                                    });
                                }
                            });
                        }

                        return (
                            <>
                                {fields.length > 0 && (
                                    <div>
                                        <div
                                            onClick={() => setShowOwners((current: boolean) => !current)}
                                            style={{ marginBottom: '1rem', marginLeft: '1rem' }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <div>
                                                    <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{showOwners ? '-' : '+'}</span>
                                                    {`Ownerships (${fields.length})`}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>{ownershipInterest(totalInterest, totalAcres)}</div>
                                            </div>
                                        </div>
                                        <AnimateHeight duration={300} height={showOwners ? 'auto' : 0}>
                                            {fields.map((name: string, index: number) => {
                                                return (
                                                    <SurfaceOwnershipCard
                                                        key={name}
                                                        formApi={props.formApi}
                                                        surfaceTractId={props.id}
                                                        onOwnershipDeleted={() => fields.remove(index)}
                                                        namePrefix={`${name}`}
                                                        ownership={fields.value[index]}
                                                        categoryTypes={props.categoryTypes}
                                                        ownerLookupId={fields.value[index].ownerLookUpId}
                                                        surfaceTractAcreage={props.surfaceTractAcreage}
                                                        contactCategories={props.contactCategories}
                                                        openContactNotes={props.openContactNotes}
                                                        onAddOwnership={(parentOwnershipId: number) => addOwnership(parentOwnershipId)}
                                                        leaseBuyers={props.leaseBuyers}
                                                        openOwnershipNotes={props.openOwnershipNotes}
                                                    />
                                                );
                                            })}
                                        </AnimateHeight>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </FieldArray>
                {/* Move to EasternSufaceOwnershipCard */}
                {/* <div>
                    <div style={{ float: 'left' }}>
                        <div><span style={{ fontSize: '1.1em' }}>Owner Name</span><br />Address</div>
                    </div>
                    <div style={{ float: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div className="mr-1">
                                <FormTextBox
                                    label="Interest"
                                    name={`so[0][0].decimalInterest`}
                                />
                            </div>
                            <div>
                                <FormTextBox
                                    label="Net Acres"
                                    name={`so[0][0]ownerships[0].netAcres`}
                                />
                            </div>
                        </div>
                    </div>
                </div> */}
                <div style={{ clear: 'both' }} />
            </div>
        </Card>
    );
};

export default SurfaceTractCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/SurfaceTractCard.tsx